import {
  faChevronLeft,
  faChevronRight,
  faFilePowerpoint,
  faImage
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Divider } from '@material-ui/core';
import React, { Dispatch, SetStateAction } from 'react';
import LanguageSelect from '../../components/muiLegacy/TextFieldSelect/LanguageSelect';
import VoxelIconButton from '../../components/voxel/Buttons/VoxelIconButton';
import VoxelText from '../../components/voxel/Text/VoxelText';
import { AnalysisColorPaletteGq } from '../../graphql/projects/models/AnalysisColorPaletteGq';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { DownloadAnalysisTheme } from '../../graphql/projects/mutations/projects/useDownloadAnalysisGq';
import { AnalysisListItem } from './AnalysisList';
import AnalysisStyleButton from './AnalysisStyleButton';
import AnalysisConfigurationButton from './Downloads/AnalysisConfigurationButton';
import OngoingDownloads from './Downloads/OngoingDownloads';
import { useAnalysisDownload } from './Downloads/useAnalysisDownload';
import AnalysisFilterPanel from './Filter/AnalysisFilterPanel';
import { AnalysisFilterState } from './Filter/analysisFilterState';
import { Language } from './internationalText';

export interface AnalysisTopSectionProps {
  project: ProjectGq;
  language: Language;
  slideTheme: DownloadAnalysisTheme;
  canGoNext: boolean;
  canGoPrev: boolean;
  handleGoToNext: () => void;
  handleGoToPrev: () => void;
  analysisState: AnalysisFilterState;
  setAnalysisState: Dispatch<SetStateAction<AnalysisFilterState>>;
  setSelectedAnalysisId: (id: string) => void;
  selectedChart?: AnalysisListItem;
  charts: AnalysisListItem[];
  reload: () => Promise<unknown>;
  palettes: AnalysisColorPaletteGq[];
}

export const AnalysisTopSection: React.FC<AnalysisTopSectionProps> = ({
  project,
  language,
  slideTheme,
  canGoNext,
  canGoPrev,
  handleGoToNext,
  handleGoToPrev,
  analysisState,
  setSelectedAnalysisId,
  setAnalysisState,
  selectedChart,
  charts,
  reload,
  palettes,
}) => {
  const downloadBag = useAnalysisDownload(project, language, slideTheme);

  return (
    <div className="w-full pb-8">
      <div className="w-full flex flex-row items-center pb-2 gap-2">
        <VoxelText variant="title3" paddingBot={false} paddingTop={false}>
          Settings
        </VoxelText>
        <div className="flex-grow" />
        <div className="w-64">
          <LanguageSelect
            analysisFilterState={analysisState}
            setAnalysisFilterState={setAnalysisState}
          />
        </div>
        <AnalysisStyleButton
          project={project}
          reload={reload}
          palettes={palettes}
          analysisFilterState={analysisState}
          setAnalysisFilterState={setAnalysisState}
        />
        <AnalysisConfigurationButton
          downloadBag={downloadBag}
          project={project}
          selectedChart={selectedChart}
          setAnalysisFilterState={setAnalysisState}
          charts={charts}
          setSelectedAnalysisId={setSelectedAnalysisId}
        />
        <VoxelIconButton
          tooltip="Export as image"
          icon={<FontAwesomeIcon icon={faImage} />}
          disabled={!selectedChart}
          onClick={() =>
            downloadBag.handleDownload('screenshot', selectedChart.title + ' as PNG', [
              {
                chartId: selectedChart.downloadId,
                analysisFilter: sessionStorage.getItem(`analysis_filter_${project.id}`),
                title: selectedChart.title,
              },
            ])
          }
          size="md"
        />
        <VoxelIconButton
          tooltip="Export to Google Slides"
          icon={<FontAwesomeIcon icon={faFilePowerpoint} />}
          disabled={!selectedChart}
          onClick={() =>
            downloadBag.handleDownload('slide', selectedChart.title + ' as slide', [
              {
                chartId: selectedChart.downloadId,
                analysisFilter: sessionStorage.getItem(`analysis_filter_${project.id}`),
                title: selectedChart.title,
              },
            ])
          }
          size="md"
        />
        {!!downloadBag.downloadState.downloads.length && (
          <OngoingDownloads downloadBag={downloadBag} />
        )}
        <div className="flex flex-row items-center gap-2">
          <VoxelIconButton
            disabled={!canGoPrev}
            tooltip="Next chart (ALT + LEFT KEY)"
            icon={<FontAwesomeIcon icon={faChevronLeft} />}
            onClick={handleGoToPrev}
            size="md"
            color="white"
          />
          <VoxelIconButton
            disabled={!canGoNext}
            tooltip="Next chart (ALT + RIGHT KEY)"
            icon={<FontAwesomeIcon icon={faChevronRight} />}
            onClick={handleGoToNext}
            size="md"
            color="white"
          />
        </div>
      </div>
      <Divider />
      {selectedChart && (
        <AnalysisFilterPanel
          project={project}
          analysisFilterState={analysisState}
          setAnalysisFilterState={setAnalysisState}
          reload={reload}
        />
      )}
    </div>
  );
};

export default AnalysisTopSection;
